/**
 * 错误日志记录系统
 * 用于记录、存储和管理应用中的错误信息
 */

export interface ErrorLogEntry {
  id: string;
  timestamp: string;
  type: 'NETWORK' | 'API' | 'DATA' | 'TIMEOUT' | 'UNKNOWN';
  code?: string | number;
  message: string;
  details?: string;
  suggestion?: string;
  retryable: boolean;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  component?: string;
  userAgent?: string;
  url?: string;
  stackTrace?: string;
  context?: Record<string, any>;
}

export interface ErrorStats {
  total: number;
  byType: Record<string, number>;
  bySeverity: Record<string, number>;
  recentErrors: ErrorLogEntry[];
  errorRate: number; // 错误率（每小时）
}

class ErrorLogger {
  private static instance: ErrorLogger;
  private logs: ErrorLogEntry[] = [];
  private maxLogs = 1000; // 最大日志条数
  private storageKey = 'quantitative_analysis_error_logs';

  private constructor() {
    this.loadFromStorage();
    this.setupGlobalErrorHandler();
  }

  public static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  /**
   * 记录错误
   */
  public logError(error: Partial<ErrorLogEntry>): string {
    const errorId = this.generateErrorId();
    const logEntry: ErrorLogEntry = {
      id: errorId,
      timestamp: new Date().toISOString(),
      type: error.type || 'UNKNOWN',
      message: error.message || 'Unknown error occurred',
      retryable: error.retryable ?? true,
      severity: error.severity || 'MEDIUM',
      component: error.component,
      userAgent: navigator.userAgent,
      url: window.location.href,
      stackTrace: error.stackTrace,
      context: error.context,
      ...error
    };

    this.logs.unshift(logEntry);
    
    // 限制日志数量
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    this.saveToStorage();
    this.notifyErrorHandlers(logEntry);

    return errorId;
  }

  /**
   * 获取错误统计信息
   */
  public getErrorStats(): ErrorStats {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    
    const recentErrors = this.logs.filter(log => 
      new Date(log.timestamp) > oneHourAgo
    );

    const byType: Record<string, number> = {};
    const bySeverity: Record<string, number> = {};

    this.logs.forEach(log => {
      byType[log.type] = (byType[log.type] || 0) + 1;
      bySeverity[log.severity] = (bySeverity[log.severity] || 0) + 1;
    });

    return {
      total: this.logs.length,
      byType,
      bySeverity,
      recentErrors: recentErrors.slice(0, 10),
      errorRate: recentErrors.length
    };
  }

  /**
   * 获取所有错误日志
   */
  public getAllLogs(): ErrorLogEntry[] {
    return [...this.logs];
  }

  /**
   * 根据条件筛选错误日志
   */
  public filterLogs(filters: {
    type?: string;
    severity?: string;
    component?: string;
    startTime?: string;
    endTime?: string;
  }): ErrorLogEntry[] {
    return this.logs.filter(log => {
      if (filters.type && log.type !== filters.type) return false;
      if (filters.severity && log.severity !== filters.severity) return false;
      if (filters.component && log.component !== filters.component) return false;
      if (filters.startTime && log.timestamp < filters.startTime) return false;
      if (filters.endTime && log.timestamp > filters.endTime) return false;
      return true;
    });
  }

  /**
   * 清除错误日志
   */
  public clearLogs(): void {
    this.logs = [];
    this.saveToStorage();
  }

  /**
   * 导出错误日志
   */
  public exportLogs(): string {
    return JSON.stringify({
      exportTime: new Date().toISOString(),
      stats: this.getErrorStats(),
      logs: this.logs
    }, null, 2);
  }

  /**
   * 生成错误ID
   */
  private generateErrorId(): string {
    return `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 从本地存储加载日志
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        this.logs = data.logs || [];
      }
    } catch (error) {
      console.warn('Failed to load error logs from storage:', error);
    }
  }

  /**
   * 保存日志到本地存储
   */
  private saveToStorage(): void {
    try {
      const data = {
        lastUpdated: new Date().toISOString(),
        logs: this.logs
      };
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save error logs to storage:', error);
    }
  }

  /**
   * 设置全局错误处理器
   */
  private setupGlobalErrorHandler(): void {
    // 捕获未处理的JavaScript错误
    window.addEventListener('error', (event) => {
      this.logError({
        type: 'UNKNOWN',
        message: event.message,
        details: `${event.filename}:${event.lineno}:${event.colno}`,
        stackTrace: event.error?.stack,
        severity: 'HIGH',
        component: 'Global'
      });
    });

    // 捕获未处理的Promise拒绝
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        type: 'UNKNOWN',
        message: 'Unhandled Promise Rejection',
        details: event.reason?.toString(),
        stackTrace: event.reason?.stack,
        severity: 'HIGH',
        component: 'Global'
      });
    });
  }

  /**
   * 通知错误处理器
   */
  private notifyErrorHandlers(error: ErrorLogEntry): void {
    // 在控制台输出错误（开发环境）
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 Error Logged: ${error.id}`);
      console.error('Type:', error.type);
      console.error('Message:', error.message);
      console.error('Details:', error.details);
      console.error('Component:', error.component);
      console.error('Full Error:', error);
      console.groupEnd();
    }

    // 这里可以添加其他通知机制，如发送到监控服务
    // 例如：Sentry, LogRocket, 自定义API等
  }
}

// 导出单例实例
export const errorLogger = ErrorLogger.getInstance();

// 便捷的错误记录函数
export const logError = (error: Partial<ErrorLogEntry>): string => {
  return errorLogger.logError(error);
};

export const getErrorStats = (): ErrorStats => {
  return errorLogger.getErrorStats();
};

export const clearErrorLogs = (): void => {
  errorLogger.clearLogs();
};

export const exportErrorLogs = (): string => {
  return errorLogger.exportLogs();
};